<script lang="ts" setup>
import { ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { customFieldProps } from '../../../utils/custom-fields';
import FormCheckbox from '../../atoms/form/FormCheckbox.vue';
import CustomFieldWrapper from './CustomFieldWrapper.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import ComboBox from '../../headless/ComboBox.vue';

const props = defineProps({ ...customFieldProps });

const model = defineModel({
    type: Array,
});

const optionsWithCheckState = ref(props.options.map(option => ({
    ...option,
    checked: model.value?.includes(option.value) ?? false,
})));

const DISPLAY_AS_DROPDOWN_THRESHOLD = 5;
const comboboxModel = ref(props.options.filter(option => model.value?.includes(option.value)));
watch(comboboxModel, (newComboboxModelValue) => {
    model.value = newComboboxModelValue.length ? newComboboxModelValue.map(option => option.value) : null;
});

const rules = {
    model: {
        required: requiredIf(() => props.required),
    },
};

const v$ = useVuelidate(rules, { model }, { $registerAs: `custom-field-${props.id}` });

watch(optionsWithCheckState, (newValue) => {
    const selectedValues = newValue
        .filter(option => option.checked)
        .map(option => option.value);

    model.value = selectedValues.length ? selectedValues : null;

    // Trigger validation if needed
    v$.value.$touch();
}, { deep: true });
</script>

<template>
    <CustomFieldWrapper
        :error="v$.$error"
        :label="label"
        :required="required"
        data-qa="custom field checkbox"
    >
        <template v-if="options.length > DISPLAY_AS_DROPDOWN_THRESHOLD">
            <ComboBox
                v-model="comboboxModel"
                class="w-[328px]"
                :screen-reader-label="label"
                :options
                :status="v$.$error ? 'error' : undefined"
                :data-qa="`custom field ${id} combobox`"
            />
        </template>
        <template v-else>
            <FormCheckbox
                v-for="option in optionsWithCheckState"
                :id="'option-' + id + '-' + option.value"
                :key="option.value"
                v-model="option.checked"
                data-qa="custom field checkbox option"
            >
                {{ option.label }}
            </FormCheckbox>
        </template>
        <FormMessage
            v-if="v$.$error"
            type="error"
            data-qa="custom field checkbox required message"
        >
            <template v-if="v$.$error">
                Bitte wähle mindestens eine Option aus.
            </template>
        </FormMessage>
    </CustomFieldWrapper>
</template>
